import {
	Stack,
	Button,
	Inline,
	Spinner,
	Box,
	Text,
	Divider,
	Alert,
} from "@sembark-travel/ui/base"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import {
	Form,
	TextInputField,
	withServerErrors,
	SubmissionError,
	validateFormValues,
} from "@sembark-travel/ui/form"
import * as Validator from "yup"
import { useAuthUser } from "./../Auth"
import { passwordValidator } from "../ResetPassword/utils"
import { NewAndConfirmPasswordFields } from "../ResetPassword/NewAndConfirmPasswordFields"

// schemas
export interface IChangePasswordCredentials {
	current: string
	password: string
	password_confirmation: string
}

const changePasswordSchema = validateFormValues(
	Validator.object().shape({
		current: Validator.string().required("Current password field is required"),
		password: passwordValidator,
		password_confirmation: Validator.string()
			.oneOf([Validator.ref("password")], "Passwords must match")
			.notRequired(),
	})
)

// actions
function XHR(xhr: XHRInstance) {
	return {
		async changePassword(data: IChangePasswordCredentials): Promise<void> {
			return xhr.patch("/passwords", data)
		},
	}
}

const changePasswordInitialValues: IChangePasswordCredentials = {
	current: "",
	password: "",
	password_confirmation: "",
}

export function ChangePasswordForm({
	onSuccess,
	onCancel,
}: {
	onSuccess: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const { user, fetchUser } = useAuthUser()
	if (!user) {
		return <Spinner padding="4" alignCenter />
	}
	if (user.can_not_change_password) {
		return (
			<Stack gap="4">
				<Alert status="warning" title="Passwords Controlled by Admins">
					<Text>
						Your passwords are controlled by your account admins. Please connect
						with your Admins to update your Account's password.
					</Text>
				</Alert>
				<Divider marginY={"0"} />
				<Box>
					<Button onClick={() => onCancel()} level="primary">
						Ok, I will connect with Admins
					</Button>
				</Box>
			</Stack>
		)
	}
	return (
		<Form<IChangePasswordCredentials>
			initialValues={changePasswordInitialValues}
			validate={changePasswordSchema}
			onSubmit={withServerErrors(async (values: IChangePasswordCredentials) => {
				await XHR(xhr).changePassword(values)
				alert("Password updated successfully")
				onSuccess()
				await fetchUser()
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						{user ? (
							<input
								type="email"
								name="email"
								value={user.email}
								hidden
								readOnly
								autoComplete="username"
							/>
						) : null}
						<TextInputField
							label="Current Password"
							name="current"
							type="password"
							required
							id="current"
							autoComplete="current-password"
						/>
						<NewAndConfirmPasswordFields
							conLabel="Confirm new password"
							newLabel="New Password"
						/>
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Updating..." : "Update Password"}
							</Button>
							<Button disabled={submitting} onClick={() => onCancel()}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}
